import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Icon } from '@/components/icon';
import { featuresState } from '@/state/features';
import { lockedDialogIsOpenState } from '@/state/user';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

interface Props {
  children: React.ReactNode | React.ReactNode[];
  isLocked?: boolean;
  iconSize?: 'base' | 'md' | 'lg';
  isBlurred?: boolean;
  isCentered?: boolean;
  id?: string;
}

const Locked = ({
  children,
  isLocked = false,
  iconSize = 'base',
  isBlurred = false,
  isCentered,
  id,
  ...props
}: Props) => {
  const features = useRecoilValue(featuresState);
  const setLockedDialogIsOpen = useSetRecoilState(lockedDialogIsOpenState);

  const SHOW_USER_AUTH = !!features.userAuthentication;

  if (!isLocked || !SHOW_USER_AUTH) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const handleLockedDialogOpen = () => {
    // Analytics Event
    trackUser.event('Locked Item Clicked', { item: id || 'Item' });
    setLockedDialogIsOpen(true);
  };

  const { base, iconWrapper, inner } = styles({ isBlurred, isCentered });

  return (
    <button onClick={handleLockedDialogOpen} className={base()} type="button">
      <div className={iconWrapper()} {...props}>
        <Icon name="lock" size={iconSize} color="states.locked" />
      </div>
      <div className={inner()}>{children}</div>
    </button>
  );
};

const styles = tv({
  slots: {
    base: 'relative',
    iconWrapper: 'absolute -right-1 -top-1 z-2',
    inner: 'opacity-40 grayscale [&_*]:pointer-events-none',
  },
  variants: {
    isCentered: {
      true: {
        iconWrapper: 'absolute left-1/2 right-auto top-1/2 -translate-x-1/2 -translate-y-1/2',
      },
    },
    isBlurred: {
      true: {
        inner: 'opacity-40 blur-sm grayscale [&_*]:pointer-events-none',
      },
    },
  },
});

export { Locked };
